import React, { useState } from 'react';
import axios from 'axios';

const MobileNotesView = ({ notes, setNotes, user, API_BASE }) => {
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    is_pinned: false
  });
  const [isEditing, setIsEditing] = useState(false);

  const sortedNotes = [...notes].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
  });

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'long' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleCreateNote = async () => {
    if (!noteForm.title.trim()) {
      alert('Note title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/calendar/notes`,
        noteForm,
        { withCredentials: true }
      );
      setNotes([response.data, ...notes]);
      resetForm();
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Failed to create note');
    }
  };

  const handleUpdateNote = async () => {
    if (!noteForm.title.trim() || !selectedNote) {
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE}/calendar/notes/${selectedNote.id}`,
        noteForm,
        { withCredentials: true }
      );
      setNotes(notes.map(note => note.id === selectedNote.id ? response.data : note));
      setSelectedNote(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/calendar/notes/${noteId}`,
        { withCredentials: true }
      );
      setNotes(notes.filter(note => note.id !== noteId));
      setSelectedNote(null);
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      const response = await axios.put(
        `${API_BASE}/calendar/notes/${note.id}`,
        { ...note, is_pinned: !note.is_pinned },
        { withCredentials: true }
      );
      setNotes(notes.map(n => n.id === note.id ? response.data : n));
      if (selectedNote?.id === note.id) {
        setSelectedNote(response.data);
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const resetForm = () => {
    setNoteForm({ title: '', content: '', is_pinned: false });
    setShowNoteModal(false);
    setIsEditing(false);
  };

  const openEditMode = () => {
    if (selectedNote) {
      setNoteForm({
        title: selectedNote.title,
        content: selectedNote.content || '',
        is_pinned: selectedNote.is_pinned || false
      });
      setIsEditing(true);
    }
  };

  // Note Detail View
  if (selectedNote && !isEditing) {
    return (
      <div className="mobile-note-detail">
        {/* Header */}
        <div className="note-detail-header">
          <button className="back-btn" onClick={() => setSelectedNote(null)}>
            ‹
          </button>
          <div className="note-detail-actions">
            <button
              className={`pin-btn ${selectedNote.is_pinned ? 'pinned' : ''}`}
              onClick={() => handleTogglePin(selectedNote)}
            >
              📌
            </button>
            {selectedNote.created_by === user?.id && (
              <>
                <button className="edit-btn" onClick={openEditMode}>✏️</button>
                <button className="delete-btn" onClick={() => handleDeleteNote(selectedNote.id)}>🗑️</button>
              </>
            )}
          </div>
        </div>

        {/* Note Content */}
        <div className="note-detail-content">
          <h2 className="note-detail-title">{selectedNote.title}</h2>
          <div className="note-detail-meta">
            <span>{selectedNote.created_by_name || 'Unknown'}</span>
            <span>•</span>
            <span>{formatDate(selectedNote.updated_at || selectedNote.created_at)}</span>
          </div>
          <div className="note-detail-body">
            {selectedNote.content || 'No content'}
          </div>
        </div>
      </div>
    );
  }

  // Edit Mode
  if (isEditing && selectedNote) {
    return (
      <div className="mobile-note-edit">
        <div className="note-edit-header">
          <button className="back-btn" onClick={() => setIsEditing(false)}>
            ‹
          </button>
          <span>Edit Note</span>
          <button className="save-btn" onClick={handleUpdateNote}>Save</button>
        </div>
        <div className="note-edit-form">
          <input
            type="text"
            className="note-title-input"
            value={noteForm.title}
            onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
            placeholder="Note title"
          />
          <textarea
            className="note-content-input"
            value={noteForm.content}
            onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
            placeholder="Write your note..."
          />
        </div>
      </div>
    );
  }

  // Notes List View
  return (
    <div className="mobile-notes-view">
      {/* Header */}
      <div className="mobile-module-header">
        <div className="module-header-left">
          <div className="module-icon notes">📝</div>
          <span className="module-title">Notes</span>
        </div>
        <button className="add-btn" onClick={() => setShowNoteModal(true)}>+</button>
      </div>

      {/* Notes List */}
      <div className="notes-list">
        {sortedNotes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <div className="empty-state-title">No notes yet</div>
            <div className="empty-state-text">Create a note to share with your team</div>
            <button className="empty-state-btn" onClick={() => setShowNoteModal(true)}>
              Create Note
            </button>
          </div>
        ) : (
          sortedNotes.map(note => (
            <div
              key={note.id}
              className={`note-card ${note.is_pinned ? 'pinned' : ''}`}
              onClick={() => setSelectedNote(note)}
            >
              <div className="note-card-header">
                <span className="note-card-title">{note.title}</span>
                {note.is_pinned && <span className="pin-indicator">📌</span>}
              </div>
              <div className="note-card-preview">
                {note.content?.substring(0, 100) || 'No content'}
                {note.content?.length > 100 ? '...' : ''}
              </div>
              <div className="note-card-meta">
                <span>{note.created_by_name || 'Unknown'}</span>
                <span>•</span>
                <span>{formatDate(note.updated_at || note.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Note Creation Modal */}
      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Note</h3>
              <button className="modal-close" onClick={() => setShowNoteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                  placeholder="Enter note title"
                />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  placeholder="Write your note..."
                  rows="6"
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={noteForm.is_pinned}
                    onChange={(e) => setNoteForm({ ...noteForm, is_pinned: e.target.checked })}
                  />
                  Pin this note
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="btn-create" onClick={handleCreateNote}>Create Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileNotesView;
